import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Users, Plus, X, Trash2, Pencil, Send, Mail } from "lucide-react";
import { fmtDateTime } from "../lib/utils-app";
import RecipientsPanel from "../components/RecipientsPanel";
import BackupsPanel from "../components/BackupsPanel";
import RequiredDocsPanel from "../components/RequiredDocsPanel";
import OpenApiPanel from "../components/OpenApiPanel";

const ROLE_BADGE = {
  admin: "bg-slate-900 text-white",
  operatore: "bg-blue-100 text-blue-800",
  visualizzatore: "bg-slate-100 text-slate-700",
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error("Solo admin possono accedere a questa sezione");
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.email}?`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("Utente eliminato");
    load();
  };

  const sendRemindersNow = async () => {
    setSending(true);
    try {
      await api.post("/admin/send-reminders-now");
      toast.success("Promemoria in invio ad admin e destinatari configurati");
    } catch { toast.error("Errore invio"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-1">Amministrazione</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><Users size={28} /> Utenti dello studio</h1>
          <p className="text-slate-500 mt-1">Gestisci collaboratori con ruoli admin, operatore o visualizzatore.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
            try { await api.post("/admin/send-worker-summary"); toast.success("Riepilogo mensile lavoratori in invio a tutte le aziende"); }
            catch { toast.error("Errore invio"); }
          }} data-testid="send-worker-summary-all"
            className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 px-3 py-2.5 rounded-lg text-sm font-medium">
            <Mail size={16} /> Riepilogo lavoratori
          </button>
          <button onClick={sendRemindersNow} disabled={sending} data-testid="send-reminders"
            className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 px-3 py-2.5 rounded-lg text-sm font-medium">
            <Send size={16} /> {sending ? "Invio…" : "Invia promemoria ora"}
          </button>
          <button onClick={() => setOpen(true)} data-testid="add-user"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-lg font-medium">
            <Plus size={18} /> Nuovo utente
          </button>
        </div>
      </header>

      <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Nome</th>
              <th className="text-left px-4 py-3 font-medium">Email</th>
              <th className="text-left px-4 py-3 font-medium">Ruolo</th>
              <th className="text-left px-4 py-3 font-medium">Creato</th>
              <th className="text-right px-4 py-3 font-medium">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_BADGE[u.role] || "bg-slate-100"}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(u.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEdit(u)} className="text-slate-700 p-1.5"><Pencil size={16} /></button>
                  <button onClick={() => remove(u)} className="text-red-600 p-1.5"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">Nessun utente.</td></tr>}
          </tbody>
        </table>
      </div>

      <RecipientsPanel />
      <RequiredDocsPanel />
      <OpenApiPanel />
      <BackupsPanel />

      {open && <NewUserDialog onClose={() => setOpen(false)} onCreated={load} />}
      {edit && <EditUserDialog user={edit} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}

function NewUserDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "operatore" });
  const [sendWelcome, setSendWelcome] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/users?send_welcome=${sendWelcome}`, f);
      toast.success(sendWelcome ? "Utente creato ed email di benvenuto inviata" : "Utente creato");
      onCreated(); onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog title="Nuovo utente" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Nome completo *"><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="user-name" /></Field>
        <Field label="Email *"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="user-email" /></Field>
        <Field label="Password iniziale *"><input required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono" data-testid="user-password" /></Field>
        <Field label="Ruolo">
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="user-role">
            <option value="admin">Admin (gestione totale)</option>
            <option value="operatore">Operatore (modifica)</option>
            <option value="visualizzatore">Visualizzatore (sola lettura)</option>
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={sendWelcome} onChange={(e) => setSendWelcome(e.target.checked)} />
          <Mail size={14} /> Invia email di benvenuto con credenziali
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600">Annulla</button>
          <button type="submit" disabled={busy} data-testid="user-submit" className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-60">
            {busy ? "Creazione…" : "Crea utente"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [f, setF] = useState({ name: user.name, role: user.role, password: "" });
  const submit = async (e) => {
    e.preventDefault();
    const body = { name: f.name, role: f.role };
    if (f.password) body.password = f.password;
    await api.put(`/users/${user.id}`, body);
    toast.success("Utente aggiornato");
    onSaved(); onClose();
  };
  return (
    <Dialog title={`Modifica ${user.email}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Nome"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></Field>
        <Field label="Ruolo">
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
            <option value="admin">Admin</option><option value="operatore">Operatore</option><option value="visualizzatore">Visualizzatore</option>
          </select>
        </Field>
        <Field label="Nuova password (lascia vuoto per non cambiare)"><input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono" /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600">Annulla</button>
          <button type="submit" className="px-4 py-2 bg-slate-900 text-white rounded-lg">Salva</button>
        </div>
      </form>
    </Dialog>
  );
}

function Dialog({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-lg">{title}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-sm font-medium text-slate-700 block mb-1">{label}</label>{children}</div>;
}
